import { useEffect, useRef, useState } from "react";
import { Volume2, Square } from "lucide-react";
import { api } from "@/lib/soul";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export function SpeakButton({ text, label = "Speak it aloud", record = false, word = "", className = "" }) {
  const [speaking, setSpeaking] = useState(false);
  const [supported, setSupported] = useState(true);
  const voiceRef = useRef(null);
  const { user } = useAuth();

  useEffect(() => {
    if (typeof window === "undefined" || !window.speechSynthesis) {
      setSupported(false);
      return;
    }
    const pick = () => {
      const voices = window.speechSynthesis.getVoices();
      voiceRef.current =
        voices.find((v) => /en(-|_)?(US|GB)/i.test(v.lang) && /female|samantha|karen|moira|serena|zira/i.test(v.name)) ||
        voices.find((v) => v.lang && v.lang.toLowerCase().startsWith("en")) ||
        voices[0] ||
        null;
    };
    pick();
    window.speechSynthesis.onvoiceschanged = pick;
    return () => {
      window.speechSynthesis.onvoiceschanged = null;
      window.speechSynthesis.cancel();
    };
  }, []);

  const speak = () => {
    if (!window.speechSynthesis) return;
    if (speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
      return;
    }
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    if (voiceRef.current) u.voice = voiceRef.current;
    u.rate = 0.82;
    u.pitch = 1.0;
    u.volume = 1.0;
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    setSpeaking(true);
    window.speechSynthesis.speak(u);

    const isPaid = user && (user.tier === "seeker" || user.tier === "contributor");
    if (record && isPaid) {
      api.post("/spoken", { text, word }).then(() => {
        toast.success("Kept in your spoken words.");
      }).catch(() => {});
    }
  };

  if (!supported) return null;

  return (
    <button
      type="button"
      data-testid="speak-button"
      onClick={speak}
      className={`inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/50 text-[#D4AF37] px-5 py-2 text-sm hover:bg-[#D4AF37]/10 transition-colors duration-300 ${className}`}
    >
      {speaking ? <Square size={15} /> : <Volume2 size={16} />}
      {speaking ? "Stop" : label}
    </button>
  );
}
